import { EditorActivity } from "./EditorActivity";
import {
  useContext,
  cloneElement,
  forwardRef,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
} from "react";
import MuiDialog, { type DialogProps } from "@mui/material/Dialog";
import MuiModal, { type ModalProps } from "@mui/material/Modal";
import { Box, Typography } from "@mui/material";
import { DesignSurface } from "./DesignSurface.js";
import { useInterfaceDesign } from "./InterfaceDesign.js";
import { designTokens, interfaceFont } from "./tokens.js";

/** Shared modal chrome. Children, focus management and business callbacks belong to MUI/callers. */
export function modalChrome(colorMode: "light" | "dark") {
  const p = designTokens[colorMode];
  return {
    "& .MuiDialog-paper": {
      color: p.text,
      backgroundColor: p.surface,
      backgroundImage: "none",
      border: `1px solid ${p.border}`,
      borderRadius: "12px",
      boxShadow: "0 20px 64px rgba(0,0,0,.20)",
      maxHeight: "calc(100dvh - 48px)",
      minHeight: 0,
      fontFamily: interfaceFont,
      "&.MuiDialog-paperFullScreen": { maxHeight: "100dvh", borderRadius: 0 },
    },
    "& .MuiDialogTitle-root": {
      color: p.text,
      backgroundColor: p.chrome,
      borderBottom: `1px solid ${p.border}`,
      padding: "18px 22px",
      fontSize: 17,
      lineHeight: 1.4,
      fontWeight: 600,
      flexShrink: 0,
      "& .MuiTypography-h6": {
        color: "inherit",
        fontSize: 17,
        fontWeight: 600,
      },
      "& .MuiTypography-h5, & .MuiTypography-h4": {
        color: "inherit",
        fontSize: 17,
        fontWeight: 600,
      },
      "&[data-dialog-severity='error']": {
        color: p.error,
        backgroundColor: `color-mix(in srgb, ${p.error} 8%, ${p.surface})`,
      },
    },
    "& .MuiDialogContent-root": {
      color: p.text,
      backgroundColor: p.surface,
      padding: "20px 22px",
      minHeight: 0,
      overscrollBehavior: "contain",
      borderColor: p.border,
      "& .MuiCard-root": {
        boxShadow: "none",
        border: `1px solid ${p.border}`,
        borderRadius: "8px",
      },
      "& .MuiCardContent-root": {
        padding: "16px",
        "&:last-child": { paddingBottom: "16px" },
      },
      "& .MuiCard-root[data-modal-unframed]": {
        border: 0,
        backgroundColor: "transparent",
        "& .MuiCardContent-root": { padding: 0 },
      },
    },
    "& .MuiDialogActions-root": {
      backgroundColor: p.chrome,
      borderTop: `1px solid ${p.border}`,
      padding: "12px 22px",
      gap: "8px",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      flexShrink: 0,
      "& > :not(style) ~ :not(style)": { marginLeft: 0 },
      "& .MuiButton-root": { minHeight: 32, padding: "6px 12px", fontSize: 12 },
      "& .MuiButton-textPrimary, & .MuiButton-outlinedPrimary": {
        color: p.text,
        backgroundColor: p.surface,
        border: `1px solid ${p.border}`,
      },
    },
    "&[data-dialog-composition] .MuiDialogTitle-root, &[data-dialog-composition] .MuiDialogContent-root, &[data-dialog-composition] .MuiDialogActions-root":
      {
        padding: 0,
      },
  };
}

export const DesignDialog = forwardRef<HTMLDivElement, DialogProps>(
  function DesignDialog({ sx, ...props }, ref) {
    const active = useContext(EditorActivity);
    const { mode, colorMode } = useInterfaceDesign();
    return (
      <DesignSurface>
        <MuiDialog
          {...props}
          open={props.open && active}
          ref={ref}
          sx={[
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ...(mode === "modern" ? [modalChrome(colorMode)] : []),
          ]}
        />
      </DesignSurface>
    );
  },
);

/** Legacy Box-based modals and image lightboxes keep their original child and dismissal rules. */
export const DesignModal = forwardRef<HTMLDivElement, ModalProps>(
  function DesignModal({ sx, children, ...props }, ref) {
    const { mode, colorMode } = useInterfaceDesign();
    const p = designTokens[colorMode];
    const media =
      children.type === "img" ||
      (children.props as { component?: string }).component === "img";
    const content =
      mode === "modern" && isValidElement(children) && !media
        ? cloneElement(children as ReactElement<HTMLAttributes<HTMLElement>>, {
            role:
              (children.props as HTMLAttributes<HTMLElement>).role ?? "dialog",
            "aria-modal": true,
          })
        : children;
    return (
      <DesignSurface>
        <MuiModal
          {...props}
          ref={ref}
          sx={[
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ...(mode === "modern"
              ? [
                  {
                    "& > .MuiBox-root:not(img)": {
                      backgroundColor: p.surface,
                      color: p.text,
                      border: `1px solid ${p.border}`,
                      borderRadius: "12px",
                      boxShadow: "0 20px 64px rgba(0,0,0,.20)",
                      maxHeight: "calc(100dvh - 48px)",
                      minHeight: 0,
                      maxWidth: "min(1040px, calc(100vw - 48px))",
                      overflow: "auto",
                      padding: "24px",
                      fontFamily: interfaceFont,
                      overflowX: "hidden",
                      "& hr": { border: 0, borderTop: `1px solid ${p.border}` },
                      "& .MuiTypography-h6": { fontSize: 17, fontWeight: 600 },
                      "& [data-modal-columns]": {
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr)",
                          md: "repeat(2, minmax(0, 1fr))",
                        },
                        "& > *": { minWidth: 0 },
                      },
                    },
                  },
                ]
              : []),
          ]}
        >
          {content}
        </MuiModal>
      </DesignSurface>
    );
  },
);

/** Header for the two legacy Box-based workflow modals; no dismissal action is invented. */
export function DesignModalHeader({ title }: { title: string }) {
  const { mode, colorMode } = useInterfaceDesign();
  if (mode !== "modern") return null;
  const p = designTokens[colorMode];
  return (
    <Box
      component="header"
      sx={{
        mx: "-24px",
        mt: "-24px",
        mb: 2,
        px: 3,
        py: 2,
        backgroundColor: p.chrome,
        borderBottom: `1px solid ${p.border}`,
        flexShrink: 0,
      }}
    >
      <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, pr: 4 }}>
        {title}
      </Typography>
    </Box>
  );
}

/** Segmented modal navigation: the selection moves, the content stays still. */
export const designDialogTabsSx = {
  flexShrink: 0,
  height: 48,
  minHeight: 48,
  boxSizing: "border-box",
  p: "4px",
  bgcolor: "background.default",
  border: 1,
  borderColor: "divider",
  borderRadius: "8px",
  "& .MuiTabs-scroller, & .MuiTabs-flexContainer": {
    height: "100%",
    alignItems: "center",
  },
  "& .MuiTabs-indicator": {
    height: "100%",
    top: 0,
    bottom: "auto",
    boxSizing: "border-box",
    border: "1px solid",
    borderColor: "primary.main",
    borderRadius: "6px",
    bgcolor: "background.paper",
    transition: "left 220ms ease, width 220ms ease",
    pointerEvents: "none",
  },
  "& .MuiTab-root": {
    zIndex: 1,
    height: "100%",
    minHeight: 0,
    py: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1.3,
    borderRadius: "6px",
    fontWeight: 600,
    border: "1px solid transparent",
    textTransform: "none",
  },
  "& .MuiTab-root.Mui-selected": { color: "primary.main" },
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiTabs-indicator": { transition: "none" },
  },
} as const;
